import is from '@sindresorhus/is';
import type { ComponentType, FunctionComponent, ReactNode } from 'react';
import { Fragment } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { IconCalendarRepeat, IconCursorClick, IconSignalTower } from '~/ui/core/icons';
import { SkeletonBase } from '~/ui/core/skeleton/Skeleton';

import type { WorkTaskAutomation, WorkTaskAutomationTriggerKind } from '../../../data-contract/task-management';
import type { ProfileTaskAutomation } from '../api/profile-task-automation-adapter';
import {
    toAutomationRecommendationDisplay,
    useAutomationRecommendationsEnabled,
    useWorkTaskAutomationRecommendation,
} from '../api/use-work-task-automation-recommendation';
import {
    TASK_AUDIT_BOX_ROW_CLASS,
    TASK_AUDIT_BOX_ROWS_CLASS,
    TASK_LIST_ROW_DIVIDER_CLASS,
    TaskAuditBox,
    TaskListRowCaret,
    TaskListRowTitle,
    TaskMetaTooltip,
    taskListRowStateClass,
} from '../tile/TaskFieldPrimitives';
import { useOpenTileOfTask } from '../tile/use-open-tile-of-task';
import {
    automationAssessmentTranslationKeys,
    automationRecommendationTranslationKeys as keys,
} from './automation-assessment-translations';

type ComplexityLevel = NonNullable<WorkTaskAutomation['complexityLevel']>;

const triggers = {
    event: { description: keys.triggerEventDescription, Icon: IconSignalTower, label: keys.triggerEvent },
    manual: { description: keys.triggerManualDescription, Icon: IconCursorClick, label: keys.triggerManual },
    schedule: { description: keys.triggerScheduleDescription, Icon: IconCalendarRepeat, label: keys.triggerSchedule },
} as const satisfies Record<
    WorkTaskAutomationTriggerKind,
    { description: string; Icon: ComponentType<{ size?: number }>; label: string }
>;

const complexities = {
    high: { description: keys.complexityHighDescription, label: keys.complexityHighLabel },
    low: { description: keys.complexityLowDescription, label: keys.complexityLowLabel },
    medium: { description: keys.complexityMediumDescription, label: keys.complexityMediumLabel },
} as const satisfies Record<ComplexityLevel, { description: string; label: string }>;

const RecommendationSkeleton: FunctionComponent = () => {
    const translate = useTranslate();

    return (
        <TaskAuditBox testId="automation-recommendation-skeleton" title={translate(keys.sectionTitle)}>
            <div aria-label={translate(keys.pending)} className={TASK_AUDIT_BOX_ROWS_CLASS} role="status">
                {[0, 1, 2, 3].map((row) => (
                    <div key={row} className={TASK_AUDIT_BOX_ROW_CLASS}>
                        <SkeletonBase className="h-3 w-40" />
                        <span className="flex-1" />
                        <SkeletonBase className="h-3 w-16" />
                    </div>
                ))}
            </div>
        </TaskAuditBox>
    );
};

const LegendItem: FunctionComponent<{
    readonly label: string;
    readonly swatchClass: string;
    readonly value: number | undefined;
}> = ({ label, swatchClass, value }) => {
    const translate = useTranslate();

    if (value === undefined) {
        return null;
    }

    return (
        <span className="inline-flex items-center gap-xs">
            <span aria-hidden={true} className={`size-2 shrink-0 rounded-xxs ${swatchClass}`} />
            {label}
            <span className="tabular-nums text-text-primary">
                {translate(automationAssessmentTranslationKeys.percent, { value })}
            </span>
        </span>
    );
};

/**
 * What is automated today against what could be, as one stacked bar: the solid part is today's
 * share, the soft part the rest of the automatable share. The legend carries the numbers.
 */
const AutomationShareRow: FunctionComponent<{
    readonly automatable: number | undefined;
    readonly today: number | undefined;
}> = ({ automatable, today }) => {
    const translate = useTranslate();
    const summary = [
        today === undefined ? undefined : translate(keys.automatedTodayValue, { value: today }),
        automatable === undefined ? undefined : translate(keys.automatableValue, { value: automatable }),
    ]
        .filter((part) => part !== undefined)
        .join(', ');

    return (
        <div className="flex flex-col gap-s px-m py-m" data-testid="automation-recommendation-share">
            <div aria-label={summary} className="relative h-2 overflow-hidden rounded-full bg-border-soft" role="img">
                {automatable === undefined ? null : (
                    <span
                        className="absolute inset-y-0 left-0 rounded-full bg-accent/35"
                        style={{ width: `${Math.max(automatable, today ?? 0)}%` }}
                    />
                )}
                {today === undefined ? null : (
                    <span className="absolute inset-y-0 left-0 rounded-full bg-accent" style={{ width: `${today}%` }} />
                )}
            </div>
            <div aria-hidden={true} className="flex items-center gap-l text-body-xxs text-text-muted">
                <LegendItem label={translate(keys.automatedToday)} swatchClass="bg-accent" value={today} />
                <LegendItem label={translate(keys.automatable)} swatchClass="bg-accent/35" value={automatable} />
            </div>
        </div>
    );
};

const MetaSeparator: FunctionComponent = () => (
    <span aria-hidden={true} className="mx-s h-3 w-px shrink-0 bg-border-soft" />
);

/** How the agent starts, how complex it is and how well it matches, each explaining itself on hover. */
const AutomationMeta: FunctionComponent<{ readonly automation: WorkTaskAutomation }> = ({ automation }) => {
    const translate = useTranslate();
    const trigger = automation.instructions?.trigger;
    const triggerCopy = trigger?.kind === undefined ? undefined : triggers[trigger.kind];
    const triggerDetail = trigger?.description?.trim();
    const complexity = automation.complexityLevel === undefined ? undefined : complexities[automation.complexityLevel];
    const match = translate(keys.match, { value: Math.round(automation.score * 100) });
    const matchReasoning = automation.scoreReasoning?.trim();
    const items: { readonly key: string; readonly node: ReactNode }[] = [];

    if (triggerCopy !== undefined) {
        items.push({
            key: 'trigger',
            node: (
                <TaskMetaTooltip
                    className="text-body-xxs"
                    explanation={
                        <>
                            <span>{translate(triggerCopy.description)}</span>
                            {triggerDetail ? (
                                <span className="opacity-70">
                                    {translate(keys.triggerDetail, { detail: triggerDetail })}
                                </span>
                            ) : null}
                        </>
                    }
                    heading={translate(triggerCopy.label)}
                    testId="automation-recommendation-trigger"
                >
                    <triggerCopy.Icon size={14} />
                    {translate(triggerCopy.label)}
                </TaskMetaTooltip>
            ),
        });
    }

    if (complexity !== undefined) {
        items.push({
            key: 'complexity',
            node: (
                <TaskMetaTooltip
                    className="text-body-xxs"
                    explanation={<span>{translate(complexity.description)}</span>}
                    heading={translate(complexity.label)}
                    testId="automation-recommendation-complexity"
                >
                    {translate(complexity.label)}
                </TaskMetaTooltip>
            ),
        });
    }

    items.push({
        key: 'match',
        node: matchReasoning ? (
            <TaskMetaTooltip
                className="text-body-xxs"
                explanation={<span>{matchReasoning}</span>}
                heading={match}
                testId="automation-recommendation-match"
            >
                <span className="tabular-nums">{match}</span>
            </TaskMetaTooltip>
        ) : (
            <span className="text-body-xxs tabular-nums text-text-muted" data-testid="automation-recommendation-match">
                {match}
            </span>
        ),
    });

    return (
        <div className="mt-s flex flex-wrap items-center gap-y-xxs">
            {items.map(({ key, node }, index) => (
                <Fragment key={key}>
                    {index === 0 ? null : <MetaSeparator />}
                    {node}
                </Fragment>
            ))}
        </div>
    );
};

/**
 * One recommended agent: its name, what it does, then how it starts, how complex it is and how
 * well it matches. The name is the row's only button and stretches over the whole row, so the
 * meta tooltips stay separate controls above it instead of nesting inside a button.
 */
const AutomationRow: FunctionComponent<{
    readonly automation: WorkTaskAutomation;
    readonly isSelected: boolean;
    readonly onOpen: ((automationId: string) => void) | undefined;
}> = ({ automation, isSelected, onOpen }) => {
    const title = automation.title ?? automation.automationId;
    const summary = automation.shortDefinition?.trim() || automation.description?.trim();
    const titleClass = 'text-body-s font-normal leading-normal text-text-primary';

    return (
        <li
            className={`group relative flex items-start gap-m px-m py-m transition-colors duration-100 ease-in-out ${TASK_LIST_ROW_DIVIDER_CLASS} ${taskListRowStateClass({ isInteractive: onOpen !== undefined, isSelected })}`}
            data-automation-id={automation.automationId}
        >
            <div className="flex min-w-0 flex-1 flex-col">
                <TaskListRowTitle
                    className={titleClass}
                    isSelected={isSelected}
                    testId="automation-recommendation-open"
                    onOpen={onOpen === undefined ? undefined : () => onOpen(automation.automationId)}
                >
                    {title}
                </TaskListRowTitle>
                {summary ? <span className="mt-xxs line-clamp-2 text-body-xs text-text-muted">{summary}</span> : null}
                <AutomationMeta automation={automation} />
            </div>
            {onOpen === undefined ? null : <TaskListRowCaret isSelected={isSelected} />}
        </li>
    );
};

/**
 * Every surface opens an agent through its own tile type, the job architecture and the position
 * alignment alike, but each carries the agent and its task.
 */
const isOpenAutomationPayload = (
    payload: unknown,
): payload is { readonly automationId: string; readonly taskId: string } =>
    is.plainObject(payload) && is.string(payload['automationId']) && is.string(payload['taskId']);

const highest = (values: readonly (number | undefined)[]): number | undefined => {
    const present = values.filter((value): value is number => value !== undefined);

    return present.length === 0 ? undefined : Math.max(...present);
};

/**
 * The task's automation recommendation, read once the opened details show a task whose
 * recommendation succeeded: what is automated today against what is automatable, then one row per
 * recommended agent, best match first. Failed, unavailable or never requested recommendations
 * render nothing.
 */
export const AutomationRecommendationSection: FunctionComponent<{
    readonly onOpenAutomation: ((automationId: string) => void) | undefined;
    readonly state: ProfileTaskAutomation['recommendationState'];
    readonly taskId: string;
}> = ({ onOpenAutomation, state, taskId }) => {
    const translate = useTranslate();
    const isEnabled = useAutomationRecommendationsEnabled();
    const query = useWorkTaskAutomationRecommendation({ state, taskId });
    const display = toAutomationRecommendationDisplay({ data: query.data, isError: query.isError, state });
    const openAutomationId = useOpenTileOfTask(isOpenAutomationPayload, taskId)?.automationId;

    if (!isEnabled || display === 'hidden') {
        return null;
    }

    if (display === 'loading' || query.data === undefined) {
        return <RecommendationSkeleton />;
    }

    const { automatablePercentage, automations, currentAutomation } = query.data;
    // A task with several workflows reports today's share per workflow; the bar shows the best one,
    // matching how the automatable share is the best one.
    const today = highest(currentAutomation.map(({ automatablePercentage: percentage }) => percentage));
    const hasShare = automatablePercentage !== undefined || today !== undefined;

    if (!hasShare && automations.length === 0) {
        return null;
    }

    return (
        <div className="flex flex-col gap-s" data-testid="automation-recommendation">
            <TaskAuditBox testId="automation-recommendation-table" title={translate(keys.sectionTitle)}>
                <div className={TASK_AUDIT_BOX_ROWS_CLASS}>
                    {hasShare ? <AutomationShareRow automatable={automatablePercentage} today={today} /> : null}
                    {automations.length === 0 ? null : (
                        <ul
                            className={`m-0 list-none p-0 ${hasShare ? 'shadow-[inset_0_1px_0_var(--color-border-soft)]' : ''}`}
                        >
                            {[...automations]
                                .sort((first, second) => second.score - first.score)
                                .map((automation) => (
                                    <AutomationRow
                                        key={automation.automationId}
                                        automation={automation}
                                        isSelected={automation.automationId === openAutomationId}
                                        onOpen={onOpenAutomation}
                                    />
                                ))}
                        </ul>
                    )}
                </div>
            </TaskAuditBox>
        </div>
    );
};
